/**
 * What a logged meal actually contained, per category (#220).
 *
 * The meal chips used to read `{category.points} pts`, which was the
 * category's points-per-serving constant — the same number on every meal that
 * touched the category, and never the amount eaten. What the client wants to
 * read back is the quantity: "Greens: 3".
 *
 * The quantities live in MealLog.categoryPoints, which is keyed by category id
 * and carries no name; the names live on the meal's category relation. Neither
 * side is guaranteed complete, so this unions them rather than iterating one:
 * a category with a recorded quantity but no relation row still gets a chip,
 * and so does a relation row with no recorded quantity (without a number).
 * That is the other half of the complaint — some categories in the meal were
 * not being shown at all.
 *
 * Entries are summed per category. A custom meal logged as two food items in
 * the same category holds two categoryPoints entries, and the client ate both.
 */

/** One category's line on a meal, ready to render. */
export type MealCategoryQuantity = {
    id: string;
    name: string;
    /** Servings consumed, or null when the meal records no quantity for this category. */
    servings: number | null;
};

/** The parts of a MealLog this reads. Structural so tests need no fixtures. */
type MealCategorySource = {
    categories?: { id: string; name: string }[];
    categoryPoints?: { categoryId: string; servings?: number }[];
};

/**
 * `servings` is the amount consumed, deliberately not `countedServings`.
 * countedServings is what the daily cap allowed to score, so a client who ate
 * three servings into a two-serving ceiling would read "Greens: 2" for food
 * they did eat. The capped figure is already reflected in the meal's points
 * total, which is the number that should shrink.
 */
export const mealCategoryQuantities = (
    meal: MealCategorySource,
    fallbackNames?: Map<string, string>,
): MealCategoryQuantity[] => {
    const names = new Map(fallbackNames);
    for (const category of meal.categories ?? []) {
        names.set(category.id, category.name);
    }

    const totals = new Map<string, number>();
    for (const entry of meal.categoryPoints ?? []) {
        totals.set(entry.categoryId, (totals.get(entry.categoryId) ?? 0) + (entry.servings ?? 0));
    }

    // categoryPoints order first (recipe tag order, or the order the food
    // items were entered), then any category on the meal it did not cover.
    const ids = [...new Set([...totals.keys(), ...(meal.categories ?? []).map((c) => c.id)])];

    return ids.map((id) => {
        const servings = totals.get(id);

        return {
            id,
            name: names.get(id) ?? 'Unknown category',
            // Servings scale with portion size and routinely land on a long
            // fraction, so they are rounded for display — as the daily
            // summary does server-side.
            servings: servings === undefined ? null : Math.round(servings * 100) / 100,
        };
    });
};
